// MACHINERY V2 (Stroje) — SR detail: the right-hand metadata column (M2.6).
//
// Spec: data package §8's read-model table. Reference impls:
// design_handoff_machinery_v3/machinery-sr.jsx `SrMetaRow` / `SrPersonRow`
// (lines 207-237, 454-466) and v1 srDetail/SrCostCard.tsx + SrPeopleCard.tsx.
// Artboards 0700 / 0701 — two stacked cards, NÁKLADY then OSOBY.
//
// >>> FIX 3, THE DETAIL HALF (ticket §7 #3). <<<
// The cost is formatted from the DTO's own `Money.currency` at locale `sk-SK`,
// by IMPORTING `formatSrCost` from the M2.5 SR list (profile/tabs/srChips.tsx)
// — not by writing a second formatter. What it replaces: v1 renders the cost
// through `PriceFormat` (srDetail/SrCostCard.tsx:75), which hard-codes a "€"
// suffix (shared/components/formats/PriceFormat.tsx:10) and so labels a USD
// report in euros. `actualPartsCost` goes through the same module's
// `formatSrAmount`, because it is a bare decimal with no currency of its own
// (§8) — v1 hard-codes "€" there too (SrCostCard.tsx:99-103).
//
// THE ARTBOARD'S EYEBROW SAYS "NÁKLADY" OVER A CARD THAT ALSO HOLDS DATES, PART,
// URGENCY AND ALARM CODE. Content wins: the data package's read model puts all
// of them on this surface, and v1's SrCostCard is exactly that mix. So the card
// keeps the artboard's label and leads with the cost value, then the meta rows.
//
// EVERY ROW SELF-HIDES WHEN ITS FIELD IS EMPTY, as v1 does — except the two
// dates, where "not closed yet" is information (v1 renders the in-progress label
// in the close-date slot, SrCostCard.tsx:131-144).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { ServiceReport, ServiceReportStatusCode } from "../../models/ServiceReportsModel";
import { formatSrAmount, formatSrCost } from "../profile/tabs/srChips";
import { formatTabDate } from "../profile/tabs/tabFormat";
import { AlertGlyph, BangGlyph, CalendarGlyph, WrenchGlyph } from "../forms/FormGlyphs";
import { MetaCard, MetaRow, PeopleLabel, PersonRow } from "./srDetailAtoms";
import { SrUrgencyChip } from "./srDetailChips";

export interface SrDetailMetaProps {
  report: ServiceReport;
  /** Resolved from `GET /v1/machine-parts` by the page; undefined until it
   *  lands, or when the report carries no part. */
  affectedPartName?: string;
}

const SrDetailMeta: React.FC<SrDetailMetaProps> = ({ report, affectedPartName }) => {
  const { translate } = useTranslation("myMachinery");

  const cost = formatSrCost(report.cost);
  const solved = report.statusCode === ServiceReportStatusCode.solved;
  const closeText = formatTabDate(report.closeDate);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      <MetaCard eyebrow={translate("myMachinery:cost")}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 20,
              fontWeight: 700,
              color: cost ? "var(--ink)" : "var(--faint)",
            }}
          >
            {cost || "—"}
          </div>

          {(report.actualLaborHours != null || report.actualPartsCost != null) && (
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", minWidth: 0 }}>
              {report.actualLaborHours != null && (
                <MetaRow glyph={<CalendarGlyph />} label={translate("myMachinery:laborHours")}>
                  {`${report.actualLaborHours}h`}
                </MetaRow>
              )}
              {report.actualPartsCost != null && (
                <MetaRow glyph={<WrenchGlyph />} label={translate("myMachinery:partsCost")}>
                  {formatSrAmount(report.actualPartsCost, report.cost?.id)}
                </MetaRow>
              )}
            </div>
          )}

          <MetaRow glyph={<CalendarGlyph />} label={translate("myMachinery:openDate")}>
            {formatTabDate(report.openDate) || "—"}
          </MetaRow>

          <MetaRow glyph={<CalendarGlyph />} label={translate("myMachinery:closeDate")}>
            {closeText || (
              <span style={{ color: "var(--muted)", fontWeight: 400 }}>
                {translate("myMachinery:serviceReportStatus.inProgress")}
              </span>
            )}
          </MetaRow>

          {affectedPartName && (
            <MetaRow glyph={<WrenchGlyph />} label={translate("myMachinery:componentAffected")}>
              {affectedPartName}
            </MetaRow>
          )}

          {report.urgency != null && (
            <MetaRow glyph={<BangGlyph />} label={translate("myMachinery:urgency")}>
              <SrUrgencyChip urgency={report.urgency} />
            </MetaRow>
          )}

          {report.alarmCode && (
            <MetaRow glyph={<AlertGlyph />} label={translate("myMachinery:alarmCodeSection")}>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  padding: "2px 8px",
                  borderRadius: 5,
                  color: "color-mix(in oklab, var(--red) 82%, var(--ink))",
                  background: "color-mix(in oklab, var(--red) 10%, var(--card))",
                  border: "1px solid color-mix(in oklab, var(--red) 30%, var(--border))",
                }}
              >
                {report.alarmCode}
              </span>
            </MetaRow>
          )}

          {report.providerCompanyName && (
            <MetaRow glyph={<WrenchGlyph />} label={translate("myMachinery:serviceProvider")}>
              {report.providerCompanyName}
            </MetaRow>
          )}
        </div>
      </MetaCard>

      {(report.createdByName || report.solvedByName) && (
        <MetaCard eyebrow={translate("myMachinery:srDetail.people")}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
            {report.createdByName && (
              <div>
                <PeopleLabel text={translate("myMachinery:reportedBy")} />
                <PersonRow
                  name={report.createdByName}
                  role={report.createdByRole}
                  at={report.openDate}
                  token="--mod-accent"
                />
              </div>
            )}
            {report.solvedByName && (
              <div>
                <PeopleLabel text={translate("myMachinery:resolvedBy")} />
                <PersonRow
                  name={report.solvedByName}
                  role={report.solvedByRole}
                  at={solved ? report.closeDate : undefined}
                  token="--green"
                />
              </div>
            )}
          </div>
        </MetaCard>
      )}
    </div>
  );
};

export default SrDetailMeta;
